import React, { useMemo, useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { useChartData } from '@/hooks/useChartDataNew';
import { applyThemeToStyle, resolveColorTheme } from '@/lib/dashboardThemes';
import { inferRegisteredMapId } from '@/lib/choroplethMapUtils';
import { ensureMapRegistered } from '@/lib/echartsGeoMaps';
import { buildChartOptions, formatValue } from '@/lib/chartOptionsBuilderNew';
import { WidgetInsights } from './WidgetInsights';
import { FilterWidget } from './FilterWidget';
import { PageHeaderWidget } from './PageHeaderWidget';
import { Image } from 'lucide-react';
import { format as formatDFns, isValid } from 'date-fns';

/*
  WidgetRenderer (Refactored)
  - Delegates logic to 'lib/chartOptionsBuilder.js'
  - Adapts current 'widget' config to builder format
  - Supports Table chart rendering with HTML tables
*/

// Image Widget Component
const ImageDisplay = ({ config }) => {
    const {
        imageUrl = '',
        objectFit = 'contain',
        align = 'center',
        opacity = 100,
        borderRadius = 0,
        showBorder = false,
        borderColor = '#d6d6d6',
    } = config || {};

    const justifyMap: Record<string, string> = { left: 'flex-start', center: 'center', right: 'flex-end' };

    if (!imageUrl) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-[#a19f9d] text-xs p-4 bg-[#fafafa] border border-dashed border-[#d6d6d6]">
                <Image className="w-8 h-8 mb-2 opacity-30" />
                <span className="font-semibold text-[#605e5c]">No image configured</span>
                <span className="mt-1 text-[10px] text-center">Select this widget and paste a URL in the Format panel →</span>
            </div>
        );
    }

    return (
        <div
            className="h-full w-full flex overflow-hidden"
            style={{ justifyContent: justifyMap[align] || 'center', alignItems: 'center' }}
        >
            <img
                src={imageUrl}
                alt="Dashboard image"
                style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: objectFit === 'fill' ? '100%' : undefined,
                    height: objectFit === 'fill' ? '100%' : undefined,
                    objectFit: objectFit as any,
                    opacity: opacity / 100,
                    borderRadius: `${borderRadius}px`,
                    border: showBorder ? `1px solid ${borderColor}` : 'none',
                    display: 'block',
                }}
                onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
            />
        </div>
    );
};

const KPIDisplay = ({ data, config, theme }) => {
    const row = data?.[0] || {};
    const kpiStyle = config.style?.kpi || {};

    // 2. Identify Fields
    const mainField = config.yAxis?.[0]; // Main Metric
    const targetField = config.target?.[0];
    const compareField = config.comparison?.[0];

    // 3. Extract Values
    const valKey = mainField ? (mainField.id || mainField.name || mainField.field) : null;
    const val = valKey ? row[valKey] : null;

    const targetKey = targetField ? (targetField.id || targetField.name || targetField.field) : null;
    const target = targetKey ? row[targetKey] : null;

    const compKey = compareField ? (compareField.id || compareField.name || compareField.field) : null;
    const compare = compKey ? (row[compKey + '_prev'] !== undefined ? row[compKey + '_prev'] : row[compKey]) : null;

    // 4. Formatting
    const valFmt = mainField ? formatValue(val, config.style?.seriesFormats?.[mainField.id] || config.style?.dataLabelFormat || 'compact') : '-';

    // 5. Styles
    const alignClass = kpiStyle.align === 'left' ? 'items-start' : kpiStyle.align === 'right' ? 'items-end' : 'items-center';

    // Metric Name Styles
    const nameStyle = {
        fontSize: (kpiStyle.name?.fontSize || 12) + 'px',
        color: kpiStyle.name?.color || theme?.widgetSubtext || '#64748b',
        fontStyle: kpiStyle.name?.italic ? 'italic' : 'normal',
        fontWeight: kpiStyle.name?.bold ? 'bold' : '500',
    };

    // Metric Value Styles
    const valueStyle = {
        fontSize: (kpiStyle.value?.fontSize || 60) + 'px',
        color: kpiStyle.value?.color || theme?.widgetText || '#1e293b',
        fontStyle: kpiStyle.value?.italic ? 'italic' : 'normal',
        fontWeight: kpiStyle.value?.bold !== false ? 'bold' : 'normal',
        lineHeight: 1.1,
    };

    // 6. Delta Logic
    let delta = null;
    let deltaColor = 'text-slate-500';
    let deltaIcon = '';

    if (kpiStyle.showTrend !== false && compare !== null && compare !== undefined) {
        const cNum = Number(compare);
        const vNum = Number(val);
        let change = 0;

        if (!isNaN(cNum)) {
            if (Math.abs(cNum) <= 10 && (config.style?.comparisonMode === 'ratio' || compareField?.format === 'percent')) {
                change = cNum;
            } else if (!isNaN(vNum) && cNum !== 0) {
                change = (vNum - cNum) / cNum;
            }
        }

        const pct = (change * 100).toFixed(1);
        const isPos = change >= 0;
        delta = `${isPos ? '+' : ''}${pct}%`;
        deltaIcon = isPos ? '↑' : '↓';

        // Trend Colors
        if (kpiStyle.trendColor === 'neutral') {
            deltaColor = 'text-blue-500 dark:text-blue-400';
        } else if (kpiStyle.trendColor === 'inverse') {
            deltaColor = isPos ? 'text-red-500 dark:text-red-400' : 'text-green-600 dark:text-green-400';
        } else {
            // Normal
            deltaColor = isPos ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400';
        }
    }

    return (
        <div className={`flex flex-col ${alignClass} justify-center h-full p-4 bg-transparent`}>
            {/* Metric Name */}
            <div
                className="uppercase tracking-wider mb-1"
                style={nameStyle}
            >
                {config.title || config.style?.seriesFormats?.[mainField?.id]?.displayName || mainField?.name || 'KPI'}
            </div>

            {/* Metric Value */}
            <div
                className="my-2"
                style={valueStyle}
            >
                {valFmt}
            </div>

            {/* Trend / Delta */}
            {delta && (
                <div className={`flex items-center gap-1 text-sm font-bold ${deltaColor} bg-slate-50 dark:bg-slate-900/60 px-2 py-1 rounded`}>
                    <span>{deltaIcon}</span>
                    <span>{delta}</span>
                    <span className="text-slate-400 dark:text-slate-500 font-normal ml-1">vs prev</span>
                </div>
            )}
        </div>
    );
};

// RAG Status Component
const RAGStatusDisplay = ({ data, config, theme }) => {
    const row = data?.[0] || {};

    // Fields
    const mainField = config.yAxis?.[0];
    const minField = config.min?.[0]; // Red Threshold (Lower Bound)
    const maxField = config.max?.[0]; // Green Threshold (Upper Bound)
    const targetField = config.target?.[0]; // Target Goal (for comparison)

    // Values
    const val = mainField ? Number(row[mainField.id || mainField.name || mainField.field]) : 0;
    // Manual Override Logic: Value > Style Config > Default
    const min = minField ? Number(row[minField.id || minField.name || minField.field]) : (config.style?.gauge?.min ?? null);
    const max = maxField ? Number(row[maxField.id || maxField.name || maxField.field]) : (config.style?.gauge?.max ?? null);
    const target = targetField ? Number(row[targetField.id || targetField.name || targetField.field]) : (config.style?.gauge?.target ?? null);

    // Logic: Red < Min < Amber < Max < Green
    // Default to 'Green' if no thresholds, or 'Gray'
    // Configs
    const sentiment = config.style?.rag?.sentiment || 'normal';
    const criticalColor = config.style?.rag?.criticalColor || '#ef4444';
    const warningColor = config.style?.rag?.warningColor || '#f59e0b';
    const healthyColor = config.style?.rag?.healthyColor || '#22c55e';
    const neutralColor = '#64748b'; // slate-500

    const iconShape = config.style?.rag?.iconShape || 'circle';
    const iconSize = config.style?.rag?.iconSize || 64; // Default 64px
    const showLabel = config.style?.rag?.showLabel !== false; // Metric Name
    const showValue = config.style?.rag?.showValue !== false;
    const showStatusText = config.style?.rag?.showStatusText !== false; // "Healthy", etc.

    let iconClass = 'rounded-full'; // Default Circle
    if (iconShape === 'square') iconClass = 'rounded-none';
    if (iconShape === 'rounded') iconClass = 'rounded-lg';

    let status = 'neutral';
    let label = 'Neutral';
    let finalColor = neutralColor;

    // Logic: 
    // Normal: < Min (Critical), >= Min & < Max (Warning), >= Max (Healthy)
    // Inverse: < Min (Healthy), >= Min & < Max (Warning), >= Max (Critical)

    if (min !== null && max !== null) {
        if (sentiment === 'normal') {
            if (val < min) { status = 'critical'; label = 'Critical'; finalColor = criticalColor; }
            else if (val >= min && val < max) { status = 'warning'; label = 'Warning'; finalColor = warningColor; }
            else { status = 'success'; label = 'Healthy'; finalColor = healthyColor; }
        } else {
            // Inverse
            if (val < min) { status = 'success'; label = 'Healthy'; finalColor = healthyColor; }
            else if (val >= min && val < max) { status = 'warning'; label = 'Warning'; finalColor = warningColor; }
            else { status = 'critical'; label = 'Critical'; finalColor = criticalColor; }
        }
    } else if (target !== null) {
        // Binary logic if only target
        // Normal: >= Target (Success)
        // Inverse: >= Target (Critical) - e.g. Expenses exceeding budget
        if (val >= target) {
            status = sentiment === 'normal' ? 'success' : 'critical';
            label = sentiment === 'normal' ? 'On Track' : 'Off Track';
            finalColor = sentiment === 'normal' ? healthyColor : criticalColor;
        } else {
            status = sentiment === 'normal' ? 'critical' : 'success';
            label = sentiment === 'normal' ? 'Off Track' : 'On Track';
            finalColor = sentiment === 'normal' ? criticalColor : healthyColor;
        }
    }

    const valFmt = mainField ? formatValue(val, config.style?.seriesFormats?.[mainField.id] || 'compact') : '-';

    return (
        <div className="flex flex-col items-center justify-center h-full p-4 bg-transparent">
            {/* Status Light */}
            <div
                className={`${iconClass} shadow-lg mb-4 flex items-center justify-center text-white font-bold text-xs ring-4 ring-offset-2 ring-slate-100 dark:ring-slate-800 dark:ring-offset-slate-950`}
                style={{ backgroundColor: finalColor, width: iconSize, height: iconSize }}
            >
                {showStatusText && label}
            </div>

            {showValue && (
                <div className="text-3xl font-bold text-slate-800 dark:text-slate-100" style={{ color: theme?.widgetText }}>
                    {valFmt}
                </div>
            )}

            {showLabel && (
                <div className="text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wider mt-1" style={{ color: theme?.widgetSubtext }}>
                    {config.title || config.style?.seriesFormats?.[mainField?.id]?.displayName || mainField?.name || 'Metric'}
                </div>
            )}

            {/* Threshold Context */}
            {(min !== null || target !== null) && (
                <div className="mt-4 flex gap-4 text-xs text-slate-400 dark:text-slate-500">
                    {min !== null && <span>Min: {formatValue(min, 'compact')}</span>}
                    {max !== null && <span>Max: {formatValue(max, 'compact')}</span>}
                    {target !== null && <span>Target: {formatValue(target, 'compact')}</span>}
                </div>
            )}
        </div>
    );
};

// Table Component for Table Chart Type
const TableDisplay = ({ data, config, style, isPivot, theme }) => {
    const [page, setPage] = useState(0);
    const [sortColumn, setSortColumn] = useState(style?.table?.defaultSortColumn || null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

    const rowsPerPage = style?.table?.pageSize || 10;

    // Handle column resize
    const startResize = (e: React.MouseEvent, col: string) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.pageX;
        const thElement = (e.target as HTMLElement).closest('th');
        const startWidth = columnWidths[col] || (thElement ? thElement.getBoundingClientRect().width : 100);

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const currentX = moveEvent.pageX;
            const newWidth = Math.max(40, startWidth + (currentX - startX));
            setColumnWidths(prev => ({ ...prev, [col]: newWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    if (!data || data.length === 0) return <div className="p-4 text-slate-400 text-xs">No Data</div>;

    // Get columns from config or infer from data
    let columns = config?.columns?.map(c => typeof c === 'string' ? c : c?.name) || Object.keys(data[0]);

    // Read style settings
    const striped = style?.table?.striped !== false;
    const showBorders = style?.table?.showBorders !== false;
    const compact = style?.table?.compact || false;
    const enableSorting = style?.table?.enableSorting !== false;
    const enableSearch = style?.table?.enableSearch || false;

    // Sync default sort column from style when it changes
    useEffect(() => {
        const defaultCol = style?.table?.defaultSortColumn;
        if (defaultCol && defaultCol !== sortColumn) {
            setSortColumn(defaultCol);
        }
    }, [style?.table?.defaultSortColumn]);

    // Get column formatting config
    const columnFormats = style?.columnFormats || {};

    // Get column alias for header display
    const getColumnAlias = (col) => {
        if (columnFormats[col]?.alias) return columnFormats[col].alias;

        // Handle fully qualified table columns from DataModels (e.g. 'dbo_DimCalendar.MonthYear')
        if (col && typeof col === 'string' && col.includes('.')) {
            const stripped = col.split('.').pop();
            if (stripped && columnFormats[stripped]?.alias) {
                return columnFormats[stripped].alias;
            }
        }

        return col;
    };

    // Pivot Specific Styles
    const pivotStyle = style?.pivot || {};

    let workingData = data;

    // Client-side Rollup for formatted Pivot Table dates
    if (isPivot && workingData.length > 0) {
        const rStyle = pivotStyle.row || {};
        const pColStyle = pivotStyle.col || {};
        const isCustomRowDate = rStyle.dateFormat === 'custom' && rStyle.datePattern;
        const isCustomColDate = pColStyle.dateFormat === 'custom' && pColStyle.datePattern;

        if (isCustomRowDate || isCustomColDate) {
            const rowHeaderKey = columns[0];
            const colFormatMap = {};
            const newColsSet = new Set();

            // Map columns to formatted column names
            columns.forEach((col, idx) => {
                if (idx === 0) {
                    newColsSet.add(col);
                    colFormatMap[col] = col;
                    return;
                }
                let formattedCol = getColumnAlias(col);
                if (isCustomColDate) {
                    try {
                        const parsedDate = new Date(formattedCol);
                        if (isValid(parsedDate)) {
                            formattedCol = formatDFns(parsedDate, pColStyle.datePattern);
                        }
                    } catch (e) { }
                }
                colFormatMap[col] = formattedCol;
                newColsSet.add(formattedCol);
            });
            columns = Array.from(newColsSet);

            // Group rows by formatted row header and sum up values in columns
            const aggregatedDataMap = new Map();
            workingData.forEach(row => {
                let rowKeyVal = row[rowHeaderKey];
                if (isCustomRowDate && rowKeyVal !== null && rowKeyVal !== undefined) {
                    try {
                        const parsedDate = new Date(rowKeyVal);
                        if (isValid(parsedDate)) {
                            rowKeyVal = formatDFns(parsedDate, rStyle.datePattern);
                        }
                    } catch (e) { }
                }

                const groupKey = String(rowKeyVal);
                if (!aggregatedDataMap.has(groupKey)) {
                    aggregatedDataMap.set(groupKey, { [rowHeaderKey]: rowKeyVal });
                }
                const aggRow = aggregatedDataMap.get(groupKey);

                Object.keys(colFormatMap).forEach((oldCol) => {
                    if (oldCol === rowHeaderKey) return;
                    const newCol = colFormatMap[oldCol];
                    const val = Number(row[oldCol]);
                    if (!isNaN(val)) {
                        aggRow[newCol] = (aggRow[newCol] || 0) + val;
                    }
                });
            });
            workingData = Array.from(aggregatedDataMap.values());
        }
    }

    // Filter data by search term
    let filteredData = workingData;
    if (enableSearch && searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        filteredData = workingData.filter(row =>
            columns.some(col => {
                const val = row[col];
                return val !== null && val !== undefined && String(val).toLowerCase().includes(term);
            })
        );
    }

    // Sort data
    let sortedData = [...filteredData];
    if (enableSorting && sortColumn) {
        sortedData.sort((a, b) => {
            const aVal = a[sortColumn];
            const bVal = b[sortColumn];

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
            if (bVal == null) return sortDirection === 'asc' ? -1 : 1;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
            }

            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();
            if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
            if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }

    // Pagination
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);
    const displayData = sortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    // Handle column header click for sorting
    const handleSort = (col) => {
        if (!enableSorting) return;
        if (sortColumn === col) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(col);
            setSortDirection('asc');
        }
        setPage(0);
    };



    // Format cell value based on column configuration
    const formatCellValue = (col, value, colIndex) => {
        if (value === null || value === undefined) return '-';

        // 1. PIVOT ROW HEADER (First Column)
        if (isPivot && colIndex === 0) {
            const rowFmt = pivotStyle.row || {};
            if (rowFmt.dateFormat === 'custom' && rowFmt.datePattern) {
                try {
                    const parsedDate = new Date(value);
                    if (isValid(parsedDate)) {
                        return formatDFns(parsedDate, rowFmt.datePattern);
                    }
                } catch (e) {
                    console.warn('Date parse error', e);
                }
                return String(value);
            }
            return String(value);
        }

        // 2. PIVOT DATA CELLS (Other Columns)
        if (isPivot && colIndex > 0) {
            const cellFmt = pivotStyle.cell || {};

            // Determine the underlying measure name for this column
            let measureName: string | null = null;

            let possibleAlias = col;
            if (col.includes('__')) {
                const parts = col.split('__');
                possibleAlias = parts[parts.length - 1];
            }

            if (config.values) {
                for (const v of config.values) {
                    const mName = typeof v === 'string' ? v : v.name;
                    // The alias generated by WidgetRenderer is mName.split('.').pop()
                    const defaultAlias = mName.split('.').pop() || mName;

                    // Also check if user set a custom alias in columnFormats
                    const customAlias = columnFormats[mName]?.alias;

                    // match by exact id/name, custom alias, or default alias
                    if (
                        possibleAlias === (v.id || mName) ||
                        possibleAlias === customAlias ||
                        possibleAlias === defaultAlias ||
                        possibleAlias === mName
                    ) {
                        measureName = mName;
                        break;
                    }
                }
            }

            if (!measureName && config.values?.length === 1) {
                // single measure fallback
                const v = config.values[0];
                measureName = typeof v === 'string' ? v : v.name;
            }

            const explicitColFormat = measureName ? columnFormats[measureName] : null;
            const formatType = explicitColFormat?.format || cellFmt.format || 'number'; // Fallback to global
            const decimals = explicitColFormat?.decimals ?? cellFmt.decimals ?? 2;

            switch (formatType) {
                case 'text': return String(value);
                case 'number': {
                    const num = parseFloat(value);
                    if (isNaN(num)) return String(value);
                    return num.toFixed(decimals);
                }
                case 'currency': {
                    const num = parseFloat(value);
                    if (isNaN(num)) return String(value);
                    return '$' + num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
                }
                case 'percentage': {
                    const num = parseFloat(value);
                    if (isNaN(num)) return String(value);
                    // Pivot values for percentage might come as 0.5 or 50.
                    // Usually raw data is 0.5.
                    const pctValue = Math.abs(num) <= 1 ? num * 100 : num;
                    return pctValue.toFixed(decimals) + '%';
                }
                case 'compact': {
                    const num = parseFloat(value);
                    if (isNaN(num)) return String(value);
                    return Intl.NumberFormat('en-US', {
                        notation: "compact",
                        maximumFractionDigits: 1
                    }).format(num);
                }
                default: return String(value);
            }
        }

        // 3. STANDARD TABLE FORMATTING
        const format = columnFormats[col] || {};
        const formatType = format.format || 'text';

        switch (formatType) {
            case 'number': {
                const num = parseFloat(value);
                if (isNaN(num)) return String(value);
                const decimals = format.decimals ?? 2;
                const formatted = num.toFixed(decimals);
                const prefix = format.prefix || '';
                const suffix = format.suffix || '';
                return `${prefix}${formatted}${suffix}`;
            }
            case 'currency': {
                const num = parseFloat(value);
                if (isNaN(num)) return String(value);
                const decimals = format.decimals ?? 2;
                const formatted = num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
                const prefix = format.prefix ?? '$';
                const suffix = format.suffix || '';
                return `${prefix}${formatted}${suffix}`;
            }
            case 'percentage': {
                const num = parseFloat(value);
                if (isNaN(num)) return String(value);
                const decimals = format.decimals ?? 2;
                // Assume value is already a percentage (not decimal) unless < 1
                const pctValue = Math.abs(num) <= 1 ? num * 100 : num;
                return `${pctValue.toFixed(decimals)}%`;
            }
            case 'url': {
                const url = String(value);
                const label = format.linkLabel || 'Open Link';
                if (url.startsWith('http') || url.startsWith('//') || url.includes('sharepoint')) {
                    return (
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                            {label}
                        </a>
                    );
                }
                return String(value);
            }
            case 'date': {
                if (value === null || value === undefined || value === '') return '';
                try {
                    const d = new Date(String(value));
                    if (isNaN(d.getTime())) return String(value); // unparseable → show raw
                    const datePattern = format.datePattern || 'medium';
                    switch (datePattern) {
                        case 'short': return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
                        case 'medium': return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        case 'long': return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                        case 'monthyear': return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                        case 'iso': return d.toISOString().split('T')[0];
                        case 'custom': {
                            const customPattern = format.customPattern || '';
                            if (!customPattern) return d.toLocaleDateString();
                            const pad = (n: number, l = 2) => String(n).padStart(l, '0');
                            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                            const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                            return customPattern
                                .replace('YYYY', String(d.getFullYear()))
                                .replace('YY', String(d.getFullYear()).slice(-2))
                                .replace('MMMM', months[d.getMonth()])
                                .replace('MMM', monthsShort[d.getMonth()])
                                .replace('MM', pad(d.getMonth() + 1))
                                .replace('DD', pad(d.getDate()))
                                .replace('HH', pad(d.getHours()))
                                .replace('mm', pad(d.getMinutes()))
                                .replace('ss', pad(d.getSeconds()));
                        }
                        default: return d.toLocaleDateString();
                    }
                } catch {
                    return String(value);
                }
            }
            default:
                return String(value);
        }
    };


    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Search Bar */}
            {enableSearch && (
                <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shrink-0">
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                        className="w-full text-xs px-2 py-1.5 border border-slate-200 dark:border-slate-800 rounded bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                </div>
            )}

            {/* Table Body */}
            <div className="flex-1 overflow-auto">
                <table className={`w-full text-xs ${showBorders ? 'border border-slate-200 dark:border-slate-800' : ''}`}>
                    <thead
                        className="sticky top-0"
                        style={{ backgroundColor: style?.headerBg || theme?.widgetBg || '#f8fafc' }}
                    >
                        <tr>
                            {columns.map((col, idx) => {
                                // Pivot Header Style
                                const isPivotHeader = isPivot;
                                const pColStyle = isPivotHeader ? (pivotStyle.col || {}) : {};

                                const cellStyle = {
                                    fontSize: isPivotHeader ? (pColStyle.fontSize ? `${pColStyle.fontSize}px` : '12px') : '0.65rem',
                                    color: isPivotHeader ? (pColStyle.color || theme?.widgetText || '#1e293b') : (style?.headerTextColor || theme?.widgetText || '#1e293b'),
                                    fontWeight: isPivotHeader ? (pColStyle.bold ? 'bold' : 'normal') : 'bold',
                                    fontStyle: isPivotHeader ? (pColStyle.italic ? 'italic' : 'normal') : 'normal',
                                };

                                const alignClass = columnFormats[col]?.align === 'center' ? 'text-center' : columnFormats[col]?.align === 'right' ? 'text-right' : 'text-left';
                                const justifyClass = columnFormats[col]?.align === 'center' ? 'justify-center' : columnFormats[col]?.align === 'right' ? 'justify-end' : 'justify-start';

                                // Also apply width correctly, ensuring table layout allows it
                                const finalStyle = {
                                    ...cellStyle,
                                    ...(columnWidths[col] ? {
                                        width: `${columnWidths[col]}px`,
                                        minWidth: `${columnWidths[col]}px`,
                                        maxWidth: `${columnWidths[col]}px`
                                    } : {})
                                };

                                return (
                                    <th
                                        key={col}
                                        onClick={() => handleSort(col)}
                                        className={`relative group ${compact ? 'px-2 py-1' : 'px-3 py-2'} uppercase tracking-wider ${showBorders ? 'border-b border-r last:border-r-0 border-slate-200 dark:border-slate-800' : ''} ${enableSorting ? 'cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-850 select-none' : ''} ${!isPivot ? alignClass : 'text-left'}`}
                                        style={finalStyle}
                                    >
                                        <div className={`flex items-center gap-1 overflow-hidden ${!isPivot ? justifyClass : 'justify-start'}`}>
                                            <span className="truncate">{getColumnAlias(col)}</span>
                                            {enableSorting && sortColumn === col && (
                                                <span className="text-blue-500 shrink-0">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                        </div>
                                        {/* Resizer Handle */}
                                        <div
                                            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onMouseDown={(e) => startResize(e, col)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {displayData.map((row, rowIdx) => {
                            const isEven = rowIdx % 2 === 1;
                            const rowBg = isEven
                                ? (style?.rowEvenBg || (theme?.id === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc'))
                                : (theme?.widgetBg || '#ffffff');

                            // Pivot Cell Styles
                            let cellStyle = {};
                            let alignClass = '';

                            return (
                                <tr
                                    key={rowIdx}
                                    className="hover:bg-blue-50 dark:hover:bg-slate-800/80 transition-colors"
                                    style={{ backgroundColor: rowBg }}
                                >
                                    {columns.map((col, cIdx) => {
                                        let alignClass = columnFormats[col]?.align === 'center' ? 'text-center' : columnFormats[col]?.align === 'right' ? 'text-right' : 'text-left';

                                        if (isPivot) {
                                            if (cIdx === 0) {
                                                // Row Header
                                                const rStyle = pivotStyle.row || {};
                                                cellStyle = {
                                                    fontSize: rStyle.fontSize ? `${rStyle.fontSize}px` : undefined,
                                                    color: rStyle.color || theme?.widgetText,
                                                    fontWeight: rStyle.bold ? 'bold' : 'normal',
                                                    fontStyle: rStyle.italic ? 'italic' : 'normal',
                                                };
                                            } else {
                                                // Data Cell
                                                const dStyle = pivotStyle.cell || {};
                                                cellStyle = {
                                                    fontSize: dStyle.fontSize ? `${dStyle.fontSize}px` : undefined,
                                                    color: dStyle.color || theme?.widgetText,
                                                };
                                                const align = dStyle.align || 'right'; // Default data right
                                                alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
                                            }
                                        } else {
                                            const colFmt = columnFormats[col] || {};
                                            cellStyle = {
                                                color: colFmt.color || theme?.widgetText,
                                            };
                                        }

                                        return (
                                            <td
                                                key={col}
                                                className={`${compact ? 'px-2 py-0.5' : 'px-3 py-1.5'} text-slate-700 dark:text-slate-200 ${showBorders ? 'border-b border-r last:border-r-0 border-slate-200 dark:border-slate-800' : ''} ${alignClass} truncate overflow-hidden`}
                                                style={{
                                                    ...cellStyle,
                                                    ...(columnWidths[col] ? {
                                                        maxWidth: `${columnWidths[col]}px`
                                                    } : {})
                                                }}
                                            >
                                                <div className="truncate" title={String(row[col] || '')}>
                                                    {formatCellValue(col, row[col], cIdx)}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {displayData.length === 0 && (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-4 text-center text-slate-400">
                                    No matching records found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-xs text-slate-500 dark:text-slate-400 shrink-0">
                    <span>{sortedData.length} rows{searchTerm ? ' (filtered)' : ''}</span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(Math.max(0, page - 1))}
                            disabled={page === 0}
                            className="px-2 py-1 rounded border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 dark:text-slate-300"
                        >
                            Prev
                        </button>
                        <span className="text-slate-650 dark:text-slate-350">{page + 1} / {totalPages}</span>
                        <button
                            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                            disabled={page >= totalPages - 1}
                            className="px-2 py-1 rounded border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 dark:text-slate-300"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

class WidgetErrorBoundary extends React.Component<any, { hasError: boolean, errorText: string }> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, errorText: '' };
    }

    static getDerivedStateFromError(error: any) {
        return { hasError: true, errorText: error?.message || 'Unknown error' };
    }

    componentDidCatch(error: any, errorInfo: any) {
        console.error("Widget render error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center h-full w-full bg-transparent text-slate-500 p-4 text-center">
                    <svg className="w-8 h-8 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    <p className="text-sm font-medium text-slate-600 mb-1">Chart Unavailable</p>
                    <p className="text-[10px] text-slate-400 max-w-[200px]">Data loading failed or the configuration is invalid. Please refresh.</p>
                </div>
            );
        }
        return this.props.children;
    }
}

const InnerWidgetRenderer = ({ widget, dataset, dataModel = null, showInsights, onToggleInsights, globalFilters, globalFilterSources = {}, onFilterChange, onFiltersResolved = null, filtersReady = true, customMeasures = [], ...rest }) => {
    const { type, config } = widget;
    const dashboardThemeId = rest.dashboardTheme || 'light';
    const resolvedTheme = resolveColorTheme(dashboardThemeId);

    const effectiveDataset = (widget.dataModelId && widget.dataModelId !== dataset?.id)
        ? { id: widget.dataModelId, name: 'Widget Dataset' }
        : dataset;

    const effectiveDataModel = (widget.dataModelId && widget.dataModelId !== dataModel?.id)
        ? { id: widget.dataModelId, name: 'Widget Data Model' }
        : dataModel;

    // Fetch Data Hook (Pass Global Filters + Custom Measures + DataModel)
    // Note: when dataModel is provided, dataset/effectiveDataset can be null
    const { data, loading, error } = useChartData(widget, effectiveDataset, globalFilters, customMeasures, effectiveDataModel, filtersReady, globalFilterSources);

    const needsGeoRegisteredMap = type === 'map' || type === 'choropleth';
    const choroplethRegisteredMapId = useMemo<'world' | 'USA' | null>(() => {
        if (!needsGeoRegisteredMap) return null;
        return inferRegisteredMapId(data || [], config?.data?.geoMap, config?.xAxis?.[0]);
    }, [needsGeoRegisteredMap, data, config?.data?.geoMap, config?.xAxis]);

    const [worldMapLoadState, setWorldMapLoadState] = React.useState<'idle' | 'loading' | 'ready' | 'error'>(
        () => (needsGeoRegisteredMap ? 'idle' : 'ready')
    );

    useEffect(() => {
        if (!needsGeoRegisteredMap) {
            setWorldMapLoadState('ready');
            return;
        }
        if (loading) return;

        let cancelled = false;
        const targetMap = choroplethRegisteredMapId || 'world';
        setWorldMapLoadState('loading');
        ensureMapRegistered(targetMap)
            .then(() => {
                if (!cancelled) setWorldMapLoadState('ready');
            })
            .catch(() => {
                if (!cancelled) setWorldMapLoadState('error');
            });
        return () => {
            cancelled = true;
        };
    }, [needsGeoRegisteredMap, loading, choroplethRegisteredMapId, widget?.id]);

    // FILTER WIDGET SHORT-CIRCUIT
    // Bypasses generic chart data loading checks because it manages its own data
    if (type === 'filter' || config.column) {
        return (
            <FilterWidget
                widget={widget}
                dataset={effectiveDataset}
                dataModel={effectiveDataModel}
                globalFilters={globalFilters}
                onFilterChange={onFilterChange}
                onFiltersResolved={onFiltersResolved}
                onWidgetUpdate={() => { }} // Read-only
                isEditMode={false}
            />
        );
    }

    // PAGE HEADER WIDGET
    if (type === 'pageHeader') {
        return (
            <div className="h-full w-full overflow-hidden">
                <PageHeaderWidget config={config} />
            </div>
        );
    }

    // TEXT WIDGET (Inline Editing)
    if (type === 'text') {
        // Extract style props
        const style = config.style || {};
        const textStyle = {
            fontSize: style.fontSize ? `${style.fontSize}px` : '14px',
            color: style.color || '#1e293b',
            fontWeight: style.bold ? 'bold' : 'normal',
            fontStyle: style.italic ? 'italic' : 'normal',
            textDecoration: style.underline ? 'underline' : 'none',
            textAlign: style.align || 'left',
            backgroundColor: style.backgroundColor || 'transparent',
            padding: style.padding ? `${style.padding}px` : '12px',
        };

        return (
            <div className="h-full w-full overflow-hidden">
                <textarea
                    className="w-full h-full resize-none border-none outline-none focus:ring-1 focus:ring-blue-500/50 rounded transition-all"
                    style={textStyle}
                    value={config.text || ''}
                    placeholder="Double click to edit text..."
                    onChange={(e) => {
                        // Immediate update for responsiveness
                        if (rest.onUpdate) {
                            rest.onUpdate(widget.id, { config: { ...config, text: e.target.value } });
                        }
                    }}
                    onMouseDown={(e) => e.stopPropagation()} // Allow interaction without dragging
                />
            </div>
        );
    }

    // IMAGE WIDGET
    if (type === 'image') {
        return (
            <div className="h-full w-full overflow-hidden">
                <ImageDisplay config={config.image || {}} />
            </div>
        );
    }

    // Cross-Filtering Logic
    const onChartClick = (params) => {
        // Feature Flag Check
        if (config.query_config?.crossFilter === false) return; // Default is true usually

        if (params.componentType === 'series') {
            const value = params.name; // Category Value
            // Determine Field: X-Axis (Axis 0) or First Legend Field
            const field = config.xAxis?.[0]?.name || config.legend?.[0]?.name;

            if (field && value !== undefined && onFilterChange) {
                // Toggle Logic
                const currentValue = globalFilters?.[field];
                let newValue;

                if (Array.isArray(currentValue)) {
                    const strValue = String(value);
                    if (currentValue.some(v => String(v) === strValue)) {
                        const filtered = currentValue.filter(v => String(v) !== strValue);
                        newValue = filtered.length > 0 ? filtered : null;
                    } else {
                        newValue = value;
                    }
                } else {
                    // Primitive - use loose comparison by coercing to string
                    newValue = String(currentValue) === String(value) ? null : value;
                }

                // Pass the source widget ID so the dashboard knows who created this filter
                onFilterChange(field, newValue, { sourceWidgetId: widget.id });
                console.log(`[CrossFilter] ${field} = ${newValue} (Source: ${widget.id})`);
            }
        }
    };

    // ECharts Events Binding
    const onEvents = useMemo(() => ({
        'click': onChartClick
    }), [config, globalFilters, onFilterChange]);

    // Memoize option generation (for non-table charts)
    const option = useMemo(() => {
        // Skip option generation for table charts
        if (type === 'table') return null;

        if (!data || data.length === 0) return null;

        // Apply theme to style
        const themeStyle = applyThemeToStyle(type, config.style || {}, resolvedTheme);

        // 1. Identify Fields
        const xField = config.xAxis?.[0]?.name;
        // const yFields = config.yAxis?.map(f => f.name) || [];

        // 2. Map Data for Builder (Builder expects 'x_val')
        const builderData = data.map(d => ({
            ...d,
            x_val:
                xField !== undefined &&
                    xField !== null &&
                    xField !== '' &&
                    (d[xField] !== undefined && d[xField] !== null)
                    ? d[xField]
                    : d.x_val
        }));

        // 3. Construct Builder Config
        // --- HEATMAP SPECIAL FIELD MAPPING ---
        // Heatmap zone layout: xAxis=X Dimension, yAxis=Y Dimension (groupField), legend=Heat Value (measure)
        // For heatmap, groupField must come from yAxis and ySeries must come from legend.
        const isHeatmap = type === 'heatmap';
        const heatmapGroupField = isHeatmap ? config.yAxis?.[0]?.name : undefined;
        const primaryMeasureSource = isHeatmap ? (config.legend || []) : (config.yAxis || []);

        // Align with useChartDataNew: API columns are s.id or `s_${index}` in request order
        // (same order as sourceSeries: yAxis, treemap color, combo lineMeasures, …).
        // Using only f.name breaks lookups when the payload key is e.g. s_0.
        let ySeriesAutoIdx = 0;
        const seriesKey = (f: { id?: string; field?: string; name?: string }) =>
            f.id || `s_${ySeriesAutoIdx++}`;
        const seriesLabel = (f: { id?: string; field?: string; name?: string; label?: string }, id: string) =>
            f.name || f.field || f.label || f.id || id;

        const builderConfig = {
            chartType: type, // Pass down for generic handling (e.g. donut)
            data: {
                // For heatmap: groupField = yAxis[0] (Y dimension). For others: legend[0] or series[0] (group-by).
                groupField: isHeatmap ? heatmapGroupField : (
                    config.legend?.[0] ? (typeof config.legend[0] === 'string' ? config.legend[0] : (config.legend[0].name || config.legend[0].field)) :
                        (config.series?.[0] ? (typeof config.series[0] === 'string' ? config.series[0] : (config.series[0].name || config.series[0].field)) : undefined)
                ),
                ySeries: [
                    ...(primaryMeasureSource.map((f) => {
                        const id = seriesKey(f);
                        return {
                            id,
                            name: seriesLabel(f, id),
                            color: f.color,
                            type: type === 'combo' ? 'bar' : undefined,
                            axis: type === 'combo' ? 'left' : 'left',
                            role: 'yAxis' as const
                        };
                    })),
                    ...(type === 'treemap'
                        ? (config.color || []).map((f) => {
                            const id = seriesKey(f);
                            return {
                                id,
                                name: seriesLabel(f, id),
                                color: f.color,
                                role: 'treemapColor' as const
                            };
                        })
                        : []),
                    ...(type === 'combo' ? (config.lineMeasures?.map((f) => {
                        const id = seriesKey(f);
                        return {
                            id,
                            name: seriesLabel(f, id),
                            color: f.color,
                            type: 'line',
                            axis: 'right',
                            role: 'yAxis' as const
                        };
                    }) || []) : []),
                    // min/max are not always present in dashboard query y_series; keep explicit keys only.
                    ...(config.min?.map((f) => {
                        const id = f.id || f.field || f.name;
                        return { id, name: seriesLabel(f, id), role: 'min' as const };
                    }) || []),
                    ...(config.max?.map((f) => {
                        const id = f.id || f.field || f.name;
                        return { id, name: seriesLabel(f, id), role: 'max' as const };
                    }) || []),
                    ...(config.target?.map((f) => {
                        const id = seriesKey(f);
                        return { id, name: seriesLabel(f, id), role: 'target' as const };
                    }) || []),
                    ...(config.comparison?.map((f) => {
                        const id = seriesKey(f);
                        return { id, name: seriesLabel(f, id), role: 'comparison' as const };
                    }) || [])
                ],
                dateFormat: config.data?.dateFormat || 'raw',
                datePattern: config.data?.datePattern || '',
                dateSeparator: config.data?.dateSeparator || '-',
                tooltipFields: config.style?.tooltip?.extraFields || [], // Fix: Read from style.tooltip.extraFields
                geoMap: config.data?.geoMap,
                geoDimensionHint: config.xAxis?.[0],
            },
            style: themeStyle
        };

        // 4. Generate Options
        try {
            return buildChartOptions(type, builderData, builderConfig);
        } catch (err) {
            console.error("Builder Error:", err);
            return null;
        }

    }, [type, config, data, dashboardThemeId]);

    // --- Snapshot Image Swap Logic ---
    const chartRef = useRef<any>(null);
    const [snapshotImgSrc, setSnapshotImgSrc] = useState<string | null>(null);

    useEffect(() => {
        if (!rest.isSnapshot) {
            setSnapshotImgSrc(null);
            return;
        }
        if (!chartRef.current || loading || !option) return;

        let cancelled = false;
        const timers: number[] = [];

        const tryCapture = () => {
            if (cancelled || !chartRef.current) return;
            try {
                const echartsInstance = chartRef.current.getEchartsInstance();
                if (!echartsInstance) return;

                const url = echartsInstance.getDataURL({
                    type: 'png',
                    pixelRatio: 2,
                    backgroundColor: '#ffffff',
                    excludeComponents: ['toolbox']
                });
                if (!cancelled && url) setSnapshotImgSrc(url);
            } catch (e) {
                if (!cancelled) {
                    console.warn("Failed to generate snapshot image:", e);
                }
            }
        };

        // Capture immediately + retries (handles async chart paint/data updates).
        tryCapture();
        timers.push(window.setTimeout(tryCapture, 120));
        timers.push(window.setTimeout(tryCapture, 320));
        timers.push(window.setTimeout(tryCapture, 700));

        // Also capture when ECharts signals render completion.
        try {
            const echartsInstance = chartRef.current.getEchartsInstance?.();
            if (echartsInstance?.on) {
                echartsInstance.on('finished', tryCapture);
            }
        } catch {
            // non-fatal
        }

        return () => {
            cancelled = true;
            timers.forEach((t) => window.clearTimeout(t));
            try {
                const echartsInstance = chartRef.current?.getEchartsInstance?.();
                echartsInstance?.off?.('finished', tryCapture);
            } catch {
                // non-fatal
            }
        };
    }, [rest.isSnapshot, loading, option, data]);
    // ---------------------------------


    // Loading State (Check FIRST - priority over error during initial load)
    if (loading) {
        return (
            <div className="flex items-center justify-center h-full" data-widget-loading="true">
                <div className="w-6 h-6 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    // Error State (Only show if not loading)
    if (error) {
        // Check if the error message is a meaningful server hint (e.g. "not materialized")
        const rawMsg: string = error?.message || error?.error || '';
        const isNotMaterialized = rawMsg.toLowerCase().includes('materialized') || rawMsg.toLowerCase().includes('build/refresh');
        const displayMsg = isNotMaterialized
            ? rawMsg.replace(/^.*?Data model has no materialized data\.\s*/i, 'Data model has no materialized data. ')
                .replace(/^.*?500:\s*/i, '')
                .split('.').slice(0, 2).join('.') + '.'
            : null;

        return (
            <div className="flex flex-col items-center justify-center h-full w-full bg-transparent text-slate-500 p-4 text-center">
                <svg className="w-8 h-8 text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                <p className="text-sm font-medium text-slate-600 mb-1">Data Unavailable</p>
                {isNotMaterialized ? (
                    <p className="text-[10px] text-amber-500 font-medium max-w-[220px]">
                        ⚠ Model not built yet — click <strong>Build/Refresh</strong> in the Data Model Designer first.
                    </p>
                ) : (
                    <p className="text-[10px] text-slate-400 max-w-[200px]">Refresh or try again</p>
                )}
            </div>
        );
    }

    if (!data || data.length === 0) {
        const isEditMode = rest?.isEditMode;
        return (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs p-4 bg-transparent" data-widget-loading={isEditMode ? 'false' : 'true'}>
                {isEditMode ? (
                    <span className="font-bold mb-1">No Data Configured</span>
                ) : (
                    <>
                        <svg className="animate-spin w-5 h-5 text-slate-300 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        <span className="font-medium text-slate-400">Loading Data...</span>
                    </>
                )}
            </div>
        );
    }

    // TABLE & PIVOT CHART: Render HTML Table (Pivot uses flat table for now)
    if (type === 'table' || type === 'pivot') {
        let renderConfig = config;
        let renderStyle = config.style;

        // PIVOT SPECIAL HANDLING: Matrix Transformation
        // This transforms "tidy" data (Row, Group, Value) into "wide" matrix data (Row, G1, G2...)
        if (type === 'pivot' && data && data.length > 0) {
            // --- Helper: extract field name from config field (string or object) ---
            const fieldName = (f: any) => typeof f === 'string' ? f : (f?.name || f?.field);

            // Row, Col, and Value fields from toolbox config
            const rowFields: string[] = (config.rows || []).map(fieldName).filter(Boolean);
            const colFields: string[] = (config.columns || []).map(fieldName).filter(Boolean);
            const valFields: string[] = (config.values || []).map(fieldName).filter(Boolean);

            // Resolve actual key used in the data row (may be prefixed like "dbo_FactCharge.ProcedureKey")
            const dataKeys = Object.keys(data[0]);
            const resolveKey = (name: string): string => {
                if (!name) return name;
                // Exact match
                if (dataKeys.includes(name)) return name;
                // Case-insensitive match
                const nameLower = name.toLowerCase();
                const exact = dataKeys.find(k => k.toLowerCase() === nameLower);
                if (exact) return exact;
                // Suffix match: "dbo_FactCharge.ProcedureKey" vs "ProcedureKey"
                const baseName = name.split('.').pop()?.toLowerCase() || '';
                const suffixMatch = dataKeys.find(k =>
                    k.split('.').pop()?.toLowerCase() === baseName ||
                    k.toLowerCase().endsWith('.' + nameLower) ||
                    nameLower.endsWith('.' + k.toLowerCase())
                );
                if (suffixMatch) return suffixMatch;
                // x_val fallback: if the field was renamed by backend (chart mode) 
                if (dataKeys.includes('x_val')) return 'x_val';
                return name;
            };

            const rowKey = rowFields.length > 0 ? resolveKey(rowFields[0]) : dataKeys[0];
            const colKey = colFields.length > 0 ? resolveKey(colFields[0]) : null;

            // If explicit values are set, try to resolve them against actual data keys.
            // BUT: when backend returns wide-format pivot data (is_pivot_wide), the columns
            // are already the unique group values (e.g. "Jan 2025", "Feb 2025") — not the
            // original field name. In that case, skip the filter and use all non-row/col keys.
            let valKeys: string[];
            const resolvedValKeys = valFields.map(resolveKey).filter(k => dataKeys.includes(k));
            const isPivotWideFormat = valFields.length > 0 && resolvedValKeys.length === 0;

            // Helper: sort pivot column keys by leading number for correct aging/period order
            // e.g. ["0-30 days", "121-180 days", "31-60 days", "60_90"] → ["0-30 days", "31-60 days", "60_90", "121-180 days"]
            const sortPivotColumns = (keys: string[]) => [...keys].sort((a, b) => {
                const na = parseFloat(a), nb = parseFloat(b);
                if (!isNaN(na) && !isNaN(nb)) return na - nb;
                return a.localeCompare(b);
            });

            if (isPivotWideFormat) {
                // Wide-format: backend already pivoted — every key except rowKey (and colKey) is a value column
                valKeys = sortPivotColumns(dataKeys.filter(k => k !== rowKey && k !== colKey));
            } else if (resolvedValKeys.length > 0) {
                valKeys = resolvedValKeys;
            } else {
                // Failsafe: grab everything else and sort
                valKeys = sortPivotColumns(dataKeys.filter(k => k !== rowKey && k !== colKey));
            }

            console.log("PIVOT DEBUG:", {
                dataKeys,
                rowKey,
                colKey,
                valKeys,
                firstRow: data[0]
            });

            let pivotData: any[] = [];
            let displayColumns: string[] = [];
            const newAliases: Record<string, any> = {};

            // MATRIX MODE: only when colKey exists in data (tidy/long format — not yet pivoted)
            // FLAT WIDE MODE: when backend already pivoted (wide format), colKey is gone from data
            const colKeyInData = colKey && dataKeys.includes(colKey);

            if (colKeyInData) {
                const rowMap = new Map<string, any>();
                const uniqueColVals = new Set<string>();

                data.forEach((row: any) => {
                    const rKey = String(row[rowKey] ?? '');
                    const cVal = String(row[colKey] ?? '');
                    uniqueColVals.add(cVal);

                    if (!rowMap.has(rKey)) rowMap.set(rKey, { __row__: rKey });

                    // For each value field, store as "colVal__valField" if multiple values, or just "colVal"
                    valKeys.forEach((vk, vi) => {
                        const colLabel = valKeys.length > 1 ? `${cVal}__${vk}` : cVal;
                        rowMap.get(rKey)[colLabel] = row[vk];
                        newAliases[colLabel] = { alias: valKeys.length > 1 ? `${cVal} (${vk.split('.').pop()})` : cVal };
                    });
                });

                const sortedCols = Array.from(uniqueColVals).sort((a, b) => {
                    const na = parseFloat(a), nb = parseFloat(b);
                    return (!isNaN(na) && !isNaN(nb)) ? na - nb : a.localeCompare(b);
                });

                const finalColKeys = sortedCols.flatMap(cVal =>
                    valKeys.length > 1 ? valKeys.map(vk => `${cVal}__${vk}`) : [cVal]
                );

                pivotData = Array.from(rowMap.values());
                displayColumns = ['__row__', ...finalColKeys];
                // Use configured display name → strip table prefix → raw key as fallback
                const rowDisplayName = (rowFields[0] || rowKey).split('.').pop() || rowFields[0] || rowKey;
                newAliases['__row__'] = { alias: rowDisplayName };

            } else {
                // === FLAT WIDE MODE ===
                // Case A: rows-only pivot (no columns, no values) — show ALL row fields as a flat table
                // Case B: wide-format with pre-aggregated values — show rowKey + valKeys
                const isRowsOnlyFlatTable = colFields.length === 0 && valFields.length === 0 && rowFields.length > 1;

                if (isRowsOnlyFlatTable) {
                    // All configured row fields become the display columns
                    const resolvedRowKeys = rowFields.map(resolveKey);
                    displayColumns = resolvedRowKeys;
                    pivotData = data.map((row: any) => {
                        const out: any = {};
                        resolvedRowKeys.forEach(rk => { out[rk] = row[rk]; });
                        return out;
                    });
                    // Strip table prefix for header display
                    resolvedRowKeys.forEach((rk, i) => {
                        const displayName = (rowFields[i] || rk).split('.').pop() || rk;
                        newAliases[rk] = { alias: displayName };
                    });
                } else {
                    // Standard flat wide: rowKey + value columns side-by-side
                    displayColumns = [rowKey, ...valKeys];
                    pivotData = data.map((row: any) => {
                        const out: any = { [rowKey]: row[rowKey] };
                        valKeys.forEach(vk => { out[vk] = row[vk]; });
                        return out;
                    });
                    // Shorten headers — strip table prefix for display
                    newAliases[rowKey] = { alias: (rowFields[0] || rowKey).split('.').pop() || rowKey };
                    valKeys.forEach(vk => {
                        newAliases[vk] = { alias: vk.split('.').pop() || vk };
                    });
                }
            }

            renderConfig = { ...config, columns: displayColumns };

            const mergedColumnFormats = { ...(config.style?.columnFormats || {}) };
            Object.keys(newAliases).forEach(k => {
                mergedColumnFormats[k] = { ...mergedColumnFormats[k], ...newAliases[k] };
            });

            renderStyle = {
                ...config.style,
                columnFormats: mergedColumnFormats
            };

            return (
                <div className="flex flex-col h-full relative group">
                    <div className="flex-1 min-h-0 overflow-hidden">
                        <TableDisplay data={pivotData} config={renderConfig} style={renderStyle} isPivot={true} theme={resolvedTheme} />
                    </div>
                </div>
            );
        }
        const rowFieldName = config.rows?.[0]?.name || config.rows?.[0] || 'Row';

        // 1. Get all available data keys (cols)
        const dataKeys = Object.keys(data[0]);

        // 2. Sort keys: x_val first, then others
        const displayColumns = [
            ...dataKeys.filter(k => k === 'x_val'),
            ...dataKeys.filter(k => k !== 'x_val')
        ];

        // 3. Override Config Columns
        renderConfig = {
            ...config,
            columns: displayColumns
        };

        // 4. Inject Alias for x_val
        renderStyle = {
            ...config.style,
            columnFormats: {
                ...(config.style?.columnFormats || {}),
                'x_val': { alias: rowFieldName }
            }
        };


        return (
            <div className="flex flex-col h-full relative group">
                <div className="flex-1 min-h-0 overflow-hidden">
                    <TableDisplay data={data} config={renderConfig} style={renderStyle} isPivot={type === 'pivot'} theme={resolvedTheme} />
                </div>
            </div>
        );
    }


    // KPI CHART
    if (type === 'kpi') {
        return (
            <div className="h-full w-full overflow-hidden relative group">
                <KPIDisplay data={data} config={config} theme={resolvedTheme} />
            </div>
        );
    }

    // RAG STATUS CHART
    if (type === 'ragKpi') {
        return (
            <div className="h-full w-full overflow-hidden relative group">
                <RAGStatusDisplay data={data} config={config} theme={resolvedTheme} />
            </div>
        );
    }



    // Other chart types: Check option was generated
    if (!option)
        return (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs p-4 text-center gap-1 max-w-[260px]">
                <span className="font-bold text-slate-600">Configuration incomplete or invalid type.</span>
                {(type === 'map' || type === 'choropleth') && !(config?.yAxis && config.yAxis.length > 0) && (
                    <span className="text-slate-500 text-[11px] mt-1">
                        Geo Map needs a numeric measure in &quot;Measure (value)&quot; to color regions; add one from Measures or tables.
                    </span>
                )}
            </div>
        );

    if (needsGeoRegisteredMap) {
        if (worldMapLoadState === 'loading' || worldMapLoadState === 'idle') {
            return (
                <div className="flex flex-col items-center justify-center h-full text-slate-400 text-xs p-4 gap-2" data-widget-loading="true">
                    <div className="w-6 h-6 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                    <span className="text-[11px] text-slate-500">Loading map…</span>
                </div>
            );
        }
        if (worldMapLoadState === 'error') {
            const mapLabel = choroplethRegisteredMapId === 'USA' ? 'U.S. states' : 'world';
            return (
                <div className="flex flex-col items-center justify-center h-full text-slate-500 p-4 text-center text-xs max-w-[240px]">
                    <span className="font-medium text-slate-700 mb-1">Map unavailable</span>
                    <span className="text-slate-400">
                        Could not load {mapLabel} map geometry from bundled assets or fallbacks (network / firewall).
                        Pin the basemap via widget{' '}
                        <code className="text-[10px] bg-slate-100 dark:bg-slate-700 px-1 rounded">config.data.geoMap</code> as usa or world.
                    </span>
                </div>
            );
        }
    }

    return (
        <div
            className="flex flex-col h-full relative group"
            data-snapshot-ready={rest.isSnapshot ? (snapshotImgSrc ? 'true' : 'false') : 'na'}
        >
            {showInsights && widget.config?.aiInsights && (
                <WidgetInsights widget={widget} data={data} onClose={() => onToggleInsights && onToggleInsights(widget.id)} />
            )}
            <div className="flex-1 min-h-0 relative">
                {/* Image Swap Strategy for Snapshot */}
                {rest.isSnapshot && snapshotImgSrc ? (
                    <img
                        src={snapshotImgSrc}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        alt="Chart Snapshot"
                    />
                ) : (
                    <ReactECharts
                        ref={chartRef}
                        key={`echarts-${dashboardThemeId}`} // Force re-mount to apply theme opts instantly
                        option={option}
                        style={{ height: '100%', width: '100%' }}
                        opts={{ renderer: 'canvas', preserveDrawingBuffer: true } as any}
                        notMerge={true}
                        onEvents={onEvents} // Event Binding
                    />
                )}
            </div>
        </div >
    );
};

export const WidgetRenderer = (props: any) => (
    <WidgetErrorBoundary>
        <InnerWidgetRenderer key={props.widget?.id ?? 'widget-instance'} {...props} />
    </WidgetErrorBoundary>
);
